/**
 * Canvas offscreen de la zone : c'est la que les traits sont appliques.
 *
 * Resolution fixe (ZONE_CANVAS_WIDTH x ratio de la zone) et independante de
 * l'ecran : le meme dessin rend a l'identique partout.
 *
 * Le canvas est transparent : le blanc du masque est pose par le Compositor.
 * C'est ce qui permet a la gomme (destination-out) de revenir au blanc sans
 * jamais reveler l'œuvre d'origine sous la zone.
 *
 * Deux couches pour tenir les 60 fps : `committed` contient les traits termines,
 * `output` = committed + trait en cours. Pendant un trait, on ne redessine que
 * ce trait-la, pas les 30 autres.
 */
import { ZONE_UNITS, type Point, type Stroke } from '@retusch/shared';
import type { Vec2 } from './geometry.js';
import { FILL_BLEED_PX, FILL_TOLERANCE, ZONE_CANVAS_WIDTH } from './renderConfig.js';

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function context2d(canvas: HTMLCanvasElement, alpha = true): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { alpha, willReadFrequently: false });
  if (ctx === null) throw new Error('contexte 2d indisponible');
  return ctx;
}

function parseColor(color: string): [number, number, number] {
  const hex = color.trim().replace('#', '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  const value = Number.parseInt(full.slice(0, 6), 16);
  if (Number.isNaN(value)) return [0, 0, 0];
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

export class ZoneCanvas {
  readonly width: number;
  readonly height: number;

  /** Traits termines. */
  private readonly committed: HTMLCanvasElement;
  private readonly committedCtx: CanvasRenderingContext2D;
  /** committed + trait en cours : c'est ce que lit le Compositor. */
  private readonly output: HTMLCanvasElement;
  private readonly outputCtx: CanvasRenderingContext2D;

  private palette: readonly string[];
  /**
   * Mode collaboratif : ce que les joueurs PRECEDENTS ont laisse sur ce
   * tableau, avec la palette de chacun.
   *
   * Repeint avant les traits du joueur courant et JAMAIS efface par l'undo :
   * on ne defait pas le travail d'un autre. Chaque couche garde sa propre
   * palette parce que les traits ne portent que l'index de leur couleur ; la
   * rendre avec la palette du joueur courant donnerait de mauvaises couleurs
   * des que l'auteur a preleve une teinte a la pipette.
   */
  private base: ReadonlyArray<{ strokes: readonly Stroke[]; palette: readonly string[] }> = [];
  private live: Stroke | null = null;
  /** Pointe du trait en cours : affichee, jamais encodee. */
  private liveTip: Point | null = null;

  constructor(zoneAspect: number, palette: readonly string[]) {
    this.width = ZONE_CANVAS_WIDTH;
    this.height = Math.max(1, Math.round(ZONE_CANVAS_WIDTH * zoneAspect));
    this.palette = palette;

    this.committed = createCanvas(this.width, this.height);
    this.committedCtx = context2d(this.committed);
    this.output = createCanvas(this.width, this.height);
    this.outputCtx = context2d(this.output);
  }

  /** Canvas a composer par-dessus le blanc de la zone. */
  get canvas(): HTMLCanvasElement {
    return this.output;
  }

  setPalette(palette: readonly string[]): void {
    this.palette = palette;
  }

  /**
   * Couleur deja posee a ce point de la zone (coordonnees zone 0-1), ou null si
   * rien n'y a ete peint. Sert a la pipette : dans la zone, elle doit rendre ce
   * que le joueur a mis, jamais l'œuvre cachee dessous.
   */
  sampleAt(point: Vec2): string | null {
    const x = Math.min(Math.max(Math.round(point.x * this.width), 0), this.width - 1);
    const y = Math.min(Math.max(Math.round(point.y * this.height), 0), this.height - 1);
    const [r, g, b, a] = this.outputCtx.getImageData(x, y, 1, 1).data;
    if ((a ?? 0) === 0) return null;
    const hex = (value: number | undefined): string => (value ?? 0).toString(16).padStart(2, '0');
    return `#${hex(r)}${hex(g)}${hex(b)}`;
  }

  /** Diametre du trait en pixels du canvas de zone. */
  strokeWidthPx(size: number): number {
    return Math.max(1, (size / ZONE_UNITS) * this.width);
  }

  /** Pose le fond herite des joueurs precedents. */
  setBase(base: ReadonlyArray<{ strokes: readonly Stroke[]; palette: readonly string[] }>): void {
    this.base = base;
    this.replay([]);
  }

  clear(): void {
    this.replay([]);
  }

  /** Rejoue tout depuis zero (utilise par l'undo). */
  replay(strokes: readonly Stroke[]): void {
    this.committedCtx.clearRect(0, 0, this.width, this.height);
    this.paintBase();
    for (const stroke of strokes) this.paint(this.committedCtx, stroke);
    this.live = null;
    this.refreshOutput();
  }

  /**
   * Repeint le fond herite. Appele par CHAQUE `replay`, donc par chaque undo :
   * c'est ce qui rend le travail des autres indestructible.
   */
  private paintBase(): void {
    const mine = this.palette;
    for (const layer of this.base) {
      // La palette bascule le temps de la couche : `paint` lit `this.palette`.
      this.palette = layer.palette;
      for (const stroke of layer.strokes) this.paint(this.committedCtx, stroke);
    }
    this.palette = mine;
  }

  /** Trait en cours : redessine sans toucher aux traits deja termines. */
  /**
   * Trait en cours, et la POINTE : la position reelle du pointeur, qui prolonge
   * le trace a l'ecran sans entrer dans les points retenus. C'est ce qui colle
   * l'encre au curseur quel que soit le pas d'echantillonnage.
   */
  setLive(stroke: Stroke | null, tip: Vec2 | null = null): void {
    this.live = stroke;
    this.liveTip = tip;
    this.refreshOutput();
  }

  /** Fige le trait en cours dans la couche committed. */
  commit(stroke: Stroke): void {
    this.paint(this.committedCtx, stroke);
    this.live = null;
    this.liveTip = null;
    this.refreshOutput();
  }

  private refreshOutput(): void {
    this.outputCtx.clearRect(0, 0, this.width, this.height);
    this.outputCtx.drawImage(this.committed, 0, 0);
    if (this.live === null) return;

    const tip = this.liveTip;
    const points =
      tip === null || tip === this.live.points[this.live.points.length - 1]
        ? this.live.points
        : [...this.live.points, tip];
    this.paint(this.outputCtx, { ...this.live, points });
  }

  private colorAt(index: number): string {
    return this.palette[index] ?? this.palette[0] ?? '#000000';
  }

  private paint(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
    if (stroke.points.length === 0) return;

    if (stroke.tool === 'fill') {
      const seed = stroke.points[0] as Point;
      this.floodFill(ctx, seed, this.colorAt(stroke.colorIndex));
      return;
    }

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = this.strokeWidthPx(stroke.size);

    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = '#000000';
      ctx.fillStyle = '#000000';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = this.colorAt(stroke.colorIndex);
      ctx.fillStyle = ctx.strokeStyle;
    }

    this.tracePath(ctx, stroke.points);
    ctx.restore();
  }

  /**
   * Lissage : on passe la courbe par les milieux de segments et on prend le
   * point capture comme point de controle. Une seule passe, pas d'allocation,
   * et les angles du capteur disparaissent.
   */
  private tracePath(ctx: CanvasRenderingContext2D, points: readonly Point[]): void {
    const toPx = (p: Point): Vec => ({ x: p.x * this.width, y: p.y * this.height });

    if (points.length === 1) {
      const only = toPx(points[0] as Point);
      ctx.beginPath();
      ctx.arc(only.x, only.y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }

    ctx.beginPath();
    const first = toPx(points[0] as Point);
    ctx.moveTo(first.x, first.y);

    if (points.length === 2) {
      const second = toPx(points[1] as Point);
      ctx.lineTo(second.x, second.y);
      ctx.stroke();
      return;
    }

    for (let i = 1; i < points.length - 1; i += 1) {
      const current = toPx(points[i] as Point);
      const next = toPx(points[i + 1] as Point);
      ctx.quadraticCurveTo(current.x, current.y, (current.x + next.x) / 2, (current.y + next.y) / 2);
    }

    const last = toPx(points[points.length - 1] as Point);
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
  }

  /**
   * Remplissage par balayage de spans, limite au canvas de zone : le pot de
   * peinture ne peut jamais deborder sur le tableau.
   */
  private floodFill(ctx: CanvasRenderingContext2D, seed: Point, color: string): void {
    const width = this.width;
    const height = this.height;
    const sx = Math.floor(seed.x * width);
    const sy = Math.floor(seed.y * height);
    if (sx < 0 || sy < 0 || sx >= width || sy >= height) return;

    const image = ctx.getImageData(0, 0, width, height);
    const data = image.data;
    const [red, green, blue] = parseColor(color);

    const start = (sy * width + sx) * 4;
    const target = [data[start] ?? 0, data[start + 1] ?? 0, data[start + 2] ?? 0, data[start + 3] ?? 0];
    if (
      target[0] === red &&
      target[1] === green &&
      target[2] === blue &&
      target[3] === 255
    ) {
      return;
    }

    const tolerance = FILL_TOLERANCE;
    const matches = (index: number): boolean =>
      Math.abs((data[index] ?? 0) - (target[0] as number)) <= tolerance &&
      Math.abs((data[index + 1] ?? 0) - (target[1] as number)) <= tolerance &&
      Math.abs((data[index + 2] ?? 0) - (target[2] as number)) <= tolerance &&
      Math.abs((data[index + 3] ?? 0) - (target[3] as number)) <= tolerance;

    // Marque des pixels reellement remplis : la bavure de bord en a besoin pour
    // savoir ou s'arreter (cf. bleedUnderEdges).
    const region = new Uint8Array(width * height);

    const stack: number[] = [sx, sy];
    while (stack.length > 0) {
      const y0 = stack.pop() as number;
      const x = stack.pop() as number;

      let y = y0;
      while (y >= 0 && matches((y * width + x) * 4)) y -= 1;
      y += 1;

      let spanLeft = false;
      let spanRight = false;
      while (y < height && matches((y * width + x) * 4)) {
        const index = (y * width + x) * 4;
        data[index] = red;
        data[index + 1] = green;
        data[index + 2] = blue;
        data[index + 3] = 255;
        region[y * width + x] = 1;

        if (x > 0) {
          const leftMatch = matches((y * width + x - 1) * 4);
          if (!spanLeft && leftMatch) {
            stack.push(x - 1, y);
            spanLeft = true;
          } else if (spanLeft && !leftMatch) {
            spanLeft = false;
          }
        }
        if (x < width - 1) {
          const rightMatch = matches((y * width + x + 1) * 4);
          if (!spanRight && rightMatch) {
            stack.push(x + 1, y);
            spanRight = true;
          } else if (spanRight && !rightMatch) {
            spanRight = false;
          }
        }
        y += 1;
      }
    }

    this.bleedUnderEdges(data, region, red, green, blue);
    ctx.putImageData(image, 0, 0);
  }

  /**
   * Glisse le remplissage SOUS le bord antialiase des traits voisins.
   *
   * Sans cela le remplissage s'arretait au premier pixel semi-transparent et le
   * fond de la zone continuait de transparaitre a travers la bande de bord :
   * un lisere clair le long de chaque trait touche.
   *
   * On ne peint pas par-dessus ces pixels, on peint DESSOUS : le resultat vaut
   * `trait x opacite + remplissage x (1 - opacite)`, exactement ce qu'il
   * vaudrait si le remplissage avait ete pose en premier. Le fondu du trait est
   * conserve au pixel pres, seul le fond qu'il laissait voir change.
   *
   * Un pixel deja OPAQUE n'est jamais touche, ce qui rend l'operation sans
   * danger : la bavure ne peut ni ronger un trait, ni le traverser pour salir
   * l'autre cote.
   *
   * On ne dilate que depuis le BORD de la region, jamais depuis son interieur :
   * un aplat de plusieurs centaines de milliers de pixels rendrait la boucle
   * inutilement quadratique alors que seul son pourtour compte.
   */
  private bleedUnderEdges(
    data: Uint8ClampedArray,
    region: Uint8Array,
    red: number,
    green: number,
    blue: number,
  ): void {
    const width = this.width;
    const height = this.height;
    const radius = FILL_BLEED_PX;
    if (radius <= 0) return;

    const ring = new Uint8Array(width * height);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const cell = y * width + x;
        if (region[cell] !== 1) continue;

        const border =
          x === 0 ||
          y === 0 ||
          x === width - 1 ||
          y === height - 1 ||
          region[cell - 1] !== 1 ||
          region[cell + 1] !== 1 ||
          region[cell - width] !== 1 ||
          region[cell + width] !== 1;
        if (!border) continue;

        for (let dy = -radius; dy <= radius; dy += 1) {
          const ny = y + dy;
          if (ny < 0 || ny >= height) continue;
          for (let dx = -radius; dx <= radius; dx += 1) {
            const nx = x + dx;
            if (nx < 0 || nx >= width) continue;
            const neighbour = ny * width + nx;
            if (region[neighbour] !== 1) ring[neighbour] = 1;
          }
        }
      }
    }

    for (let cell = 0; cell < ring.length; cell += 1) {
      if (ring[cell] !== 1) continue;
      const index = cell * 4;
      const alpha = (data[index + 3] ?? 0) / 255;
      // Deja opaque : le trait couvre entierement, il n'y a pas de fond visible
      // a corriger.
      if (alpha >= 1) continue;
      data[index] = Math.round((data[index] ?? 0) * alpha + red * (1 - alpha));
      data[index + 1] = Math.round((data[index + 1] ?? 0) * alpha + green * (1 - alpha));
      data[index + 2] = Math.round((data[index + 2] ?? 0) * alpha + blue * (1 - alpha));
      data[index + 3] = 255;
    }
  }
}

interface Vec {
  x: number;
  y: number;
}
